// The impact cue router (decision 0047: "one impact event per hit fans out to hit-stop, a flash on the struck body,
// camera shake and kick, sound, particles and rumble, each set per hit kind as data"; the Gameplay Cues model;
// suggestion "impact director"). The game registers a sink per channel and passes the cue table: kind → channel →
// that channel's numbers. `fire(event)` looks up the event's kind and calls each listed channel's sink once. A channel
// the table names with no sink is either declared sinkless (a channel kept for later, such as rumble while haptics
// are deferred: contradiction 1 of spike 002's plan) or a mistake, which `missing()` reports.
//
// The router doesn't know what a channel does: hit-stop, a flash, a shake, particles, a sound are all the game's
// sinks. Counts per kind and channel are kept, so a test can check that each hit kind reached each channel its row
// names.

/** One channel's numbers in a cue row (whatever its sink reads: seconds, trauma, an emitter id…). */
export type ChannelCue = Readonly<Record<string, unknown>>;

/** The cue table: hit kind → channel → the channel's numbers. A channel left out of a row doesn't fire for that kind. */
export type CueTable = Readonly<Record<string, Readonly<Record<string, ChannelCue>>>>;

/** A sink: does one channel's work for one event. */
export type CueSink<E> = (cue: ChannelCue, event: E) => void;

/** Counts per kind and channel: `fired` reached a sink, `sinkless` was named by the table with no sink. */
export interface CueCounts {
  fired: Record<string, Record<string, number>>;
  sinkless: Record<string, Record<string, number>>;
  /** Events whose kind had no row. */
  unknownKinds: Record<string, number>;
  events: number;
}

/** The router's options. */
export interface ImpactRouterOptions {
  cues: CueTable;
  /** Channels the table may name that have no sink on purpose (counted under `sinkless`, not reported missing). */
  sinkless?: readonly string[];
}

/** Fans one impact event out to the channels its kind names. */
export class ImpactRouter<E extends { kind: string }> {
  private sinks = new Map<string, CueSink<E>>();
  private cues: CueTable;
  private readonly sinklessSet: Set<string>;
  private c: CueCounts = { fired: {}, sinkless: {}, unknownKinds: {}, events: 0 };

  constructor(o: ImpactRouterOptions) {
    this.cues = o.cues;
    this.sinklessSet = new Set(o.sinkless ?? []);
  }

  /** Register a channel's sink (replacing any). Returns the unregister function. */
  register(channel: string, sink: CueSink<E>): () => void {
    this.sinks.set(channel, sink);
    return () => {
      if (this.sinks.get(channel) === sink) this.sinks.delete(channel);
    };
  }

  /** The registered channels. */
  channels(): string[] {
    return [...this.sinks.keys()].sort();
  }

  /** Every channel the table names, across all kinds. */
  named(): string[] {
    const s = new Set<string>();
    for (const row of Object.values(this.cues)) for (const ch of Object.keys(row)) s.add(ch);
    return [...s].sort();
  }

  /** Channels the table names that have no sink and aren't declared sinkless: a sink that was never registered. */
  missing(): string[] {
    return this.named().filter((ch) => !this.sinks.has(ch) && !this.sinklessSet.has(ch));
  }

  /** Swap the cue table (live tuning). */
  setCues(cues: CueTable): void {
    this.cues = cues;
  }

  /** Fan an event out. Returns how many sinks ran. A sink that throws is reported on the console and the others
   *  still run (one broken channel never eats the rest of the feedback). */
  fire(e: E): number {
    this.c.events++;
    const row = this.cues[e.kind];
    if (!row) {
      this.c.unknownKinds[e.kind] = (this.c.unknownKinds[e.kind] ?? 0) + 1;
      return 0;
    }
    let ran = 0;
    for (const [ch, cue] of Object.entries(row)) {
      const sink = this.sinks.get(ch);
      if (!sink) {
        const k = (this.c.sinkless[e.kind] ??= {});
        k[ch] = (k[ch] ?? 0) + 1;
        continue;
      }
      const k = (this.c.fired[e.kind] ??= {});
      k[ch] = (k[ch] ?? 0) + 1;
      try {
        sink(cue, e);
      } catch (err) {
        console.error(`[impact] channel "${ch}" failed for "${e.kind}":`, err);
      }
      ran++;
    }
    return ran;
  }

  /** The counts so far (a copy). */
  counts(): CueCounts {
    return structuredClone(this.c);
  }

  /** Zero the counts. */
  resetCounts(): void {
    this.c = { fired: {}, sinkless: {}, unknownKinds: {}, events: 0 };
  }
}
